@if (isLoading) {
  <div class="xi-loader-container__center">
    <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
  </div>
}

<ng-container *transloco="let t">
  <div class="widget-grid__outer" [class.hide]="isLoading">
    @if (dataSourceNotFound) {
      <div class="text-center">
        <h1>{{ t('widgets.data-source-removed-select-another') }}</h1>
      </div>
    }
    @if (noDocuments) {
      <div class="text-center">
        <h1>{{ t('widgets.no-data-found') }}</h1>
      </div>
    }
    @if (hasInvalidComparison) {
      <div class="text-center">
        <h1>{{ t(getComparisonErrorMessage(comparison)) }}</h1>
      </div>
    }
    @if (noPermission) {
      <div class="text-center">
        <h1>{{ t('widgets.no-permission-to-view') }}</h1>
      </div>
    }
    @if (!dataSourceNotFound && !noDocuments && !hasInvalidComparison && !noPermission) {
      @if (isComparisonMode && payload.numericMetadataFields?.length && !isNoBreakdown) {
        <div class="widget-grid__comparison-bar" (click)="$event.stopPropagation()">
          <span class="widget-grid__comparison-bar-label">{{ t('widgets.grid.showing') }}:</span>
          <button
            type="button"
            class="widget-grid__comparison-badge"
            [class.widget-grid__comparison-badge--static]="!canChangeComparisonMetadataField"
            (click)="toggleComparisonMetadataDropdown()"
            [attr.aria-disabled]="!canChangeComparisonMetadataField"
          >
            {{ selectedComparisonMetadataField?.label }}
            @if (canChangeComparisonMetadataField) {
              <xi-icon icon="keyboard_arrow_down" size="16"></xi-icon>
            }
          </button>
          @if (comparisonMetadataDropdownOpen && canChangeComparisonMetadataField) {
            <div class="widget-grid__comparison-dropdown">
              <div class="widget-grid__comparison-dropdown-header">
                <xi-icon
                  icon="search"
                  size="16"
                  class="widget-grid__comparison-dropdown-search-icon"
                ></xi-icon>
                <input
                  class="widget-grid__comparison-dropdown-search"
                  type="text"
                  [placeholder]="t('widgets.grid.change-meta-data-field')"
                  [value]="comparisonMetadataSearch()"
                  (input)="onComparisonMetadataSearchChange($any($event.target).value)"
                  autofocus
                />
                <button
                  type="button"
                  class="widget-grid__comparison-dropdown-close"
                  (click)="closeComparisonMetadataDropdown()"
                >
                  <xi-icon icon="close" size="16"></xi-icon>
                </button>
              </div>
              <div class="widget-grid__comparison-dropdown-list">
                @for (field of filteredComparisonMetadataFields(); track field.fieldId) {
                  <button
                    class="widget-grid__comparison-dropdown-item"
                    [class.widget-grid__comparison-dropdown-item--active]="
                      field.fieldId ===
                      payload.numericMetadataFields?.[
                        payload.selectedComparisonMetadataFieldIndex ?? 0
                      ]?.fieldId
                    "
                    (click)="
                      selectComparisonMetadataField(payload.numericMetadataFields?.indexOf(field))
                    "
                  >
                    <span class="widget-grid__comparison-dropdown-item-label">{{
                      field.label
                    }}</span>
                    @if (
                      field.fieldId ===
                      payload.numericMetadataFields?.[
                        payload.selectedComparisonMetadataFieldIndex ?? 0
                      ]?.fieldId
                    ) {
                      <span class="widget-grid__comparison-dropdown-item-check">
                        <xi-icon icon="check" size="12" color="white_light_15"></xi-icon>
                      </span>
                    }
                  </button>
                }
                @if (!filteredComparisonMetadataFields().length) {
                  <div class="widget-grid__comparison-dropdown-empty">
                    {{ t('widgets.grid.no-data') }}
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
    }
    <div class="widget-grid__container">
      @if (!dataSourceNotFound && !noDocuments && !hasInvalidComparison && !noPermission) {
        <table
          #table
          [id]="'gridTable-' + widget.id"
          mat-table
          [dataSource]="dataSource"
          matSort
          [matSortActive]="payload.sort.field"
          [matSortDirection]="payload.sort.dir"
          matSortDisableClear
          (matSortChange)="onSortChange($event)"
          class="widget-grid__table"
        >
          @for (column of displayedColumns; track column) {
            @if (!payload.transpose) {
              <ng-container [matColumnDef]="column">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  mat-sort-header
                  [matTooltip]="columns[column]?.tooltip"
                  matTooltipPosition="above"
                >
                  {{ getColumnHeader(column) }}
                </th>
                @if (column === 'breakdown') {
                  <td
                    mat-cell
                    *matCellDef="let gridItem"
                    class="bold"
                    [ngClass]="{ 'overflow-visible': hasCellPinnedComment(gridItem.breakdown) }"
                    [matTooltip]="
                      !hasDocumentsColumn
                        ? t('widgets.datatable.document-count-tooltip', {
                            count: gridItem.documentCount || 0,
                          })
                        : null
                    "
                    (click)="
                      gridItem.type === 'normal' &&
                        openCellMenu($event, gridItem, 'breakdown', gridItem.breakdownDisplay)
                    "
                  >
                    <div class="widget-grid__column-value-container">
                      <span class="widget-grid__column-value-wrapper">
                        {{ gridItem.breakdownDisplay }}
                      </span>
                      @if (getCellComments(gridItem.breakdown); as cellComments) {
                        @if (getPinnedComment(cellComments); as pinnedComment) {
                          <div
                            class="pinned-comment-container"
                            appPinnedCommentDrag
                            [(comment)]="pinnedComment"
                            containerClass="widget-grid__table"
                            (isDragging)="disablePinnedCommentClick = $event"
                            (click)="
                              !disablePinnedCommentClick &&
                                onCellCommentIconClicked(
                                  $event,
                                  gridItem,
                                  'breakdown',
                                  gridItem.breakdownDisplay
                                )
                            "
                          >
                            <app-comment-compact-card
                              #commentCard
                              [comment]="pinnedComment"
                              [selected]="selectedCommentPointId === pinnedComment.target.pointId"
                            ></app-comment-compact-card>
                          </div>
                        } @else {
                          <xi-icon
                            icon="comment_filled"
                            class="widget-grid__table-cell--comment-icon"
                            [class.selected]="
                              selectedCommentPointId === cellComments[0].target.pointId
                            "
                            (click)="
                              onCellCommentIconClicked(
                                $event,
                                gridItem,
                                'breakdown',
                                gridItem.breakdownDisplay
                              )
                            "
                          ></xi-icon>
                        }
                      }
                    </div>
                  </td>
                } @else if (column === 'delta') {
                  <td mat-cell *matCellDef="let gridItem">
                    <div class="widget-grid__column-value-container">
                      @if (gridItem.delta !== null && gridItem.delta !== undefined) {
                        <span
                          class="widget-grid__delta-value"
                          [class.widget-grid__delta-value--positive]="gridItem.delta > 0"
                          [class.widget-grid__delta-value--negative]="gridItem.delta < 0"
                          [class.widget-grid__delta-value--neutral]="gridItem.delta === 0"
                        >
                          <xi-icon
                            [icon]="
                              gridItem.delta > 0
                                ? 'small_up_triangle_filled'
                                : 'small_down_triangle_filled'
                            "
                            [color]="
                              gridItem.delta > 0
                                ? 'positive'
                                : gridItem.delta < 0
                                  ? 'negative'
                                  : 'neutral'
                            "
                            iconSize="small"
                          ></xi-icon>
                          {{ gridItem.delta > 0 ? '+' : ''
                          }}{{ gridItem.delta | number: getDecimalPrecisionPipe(column) }}
                        </span>
                      } @else {
                        {{ t('widgets.grid.no-data') }}
                      }
                    </div>
                  </td>
                } @else {
                  <td mat-cell *matCellDef="let gridItem" [class.bold]="gridItem.type === 'total'">
                    <div class="widget-grid__column-value-container">
                      <span
                        class="widget-grid__column-value-wrapper"
                        [class.widget-grid__column-value-wrapper--sentiment-bar]="
                          gridItem[column + '_scoring'] === 'sentimentBar'
                        "
                      >
                        @if (gridItem[column + '_scoring'] === 'sentimentBar') {
                          <app-sentiment-bar
                            [sentimentBuckets]="gridItem.sentimentBuckets"
                            [precision]="columns[column]?.precision"
                          ></app-sentiment-bar>
                        } @else {
                          {{ gridItem[column] | number: gridItem.pipe ?? columns[column]?.pipe }}

                          @if (gridItem[column + '_scoring'] === 'percentage') {
                            %
                            <app-sentiment-icon
                              [sentiment]="gridItem[column + '_sentiment']"
                            ></app-sentiment-icon>
                          }
                        }
                      </span>
                    </div>
                  </td>
                }
              </ng-container>
            } @else {
              <ng-container [matColumnDef]="column">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  mat-sort-header
                  [matTooltip]="columns[column]?.tooltip"
                  matTooltipPosition="above"
                >
                  {{ getColumnHeader(column) }}
                </th>
                <td
                  mat-cell
                  [ngClass]="{
                    'overflow-visible': hasCellPinnedComment(gridItem.fieldId + '_' + column),
                  }"
                  *matCellDef="let gridItem"
                  [class.bold]="column === TRANSPOSED_METADATA_COLUMN || column === 'data'"
                  (click)="
                    gridItem.type === 'normal' &&
                      column === TRANSPOSED_METADATA_COLUMN &&
                      openCellMenu($event, gridItem, 'fieldId', gridItem.label)
                  "
                >
                  <div class="widget-grid__column-value-container">
                    <span
                      class="widget-grid__column-value-wrapper"
                      [class.widget-grid__column-value-wrapper--sentiment-bar]="
                        gridItem.sentimentScoring === 'sentimentBar'
                      "
                    >
                      @switch (column) {
                        @case ('fieldId') {
                          {{ gridItem.label }}
                        }
                        @default {
                          @if (gridItem.sentimentScoring === 'sentimentBar') {
                            <app-sentiment-bar
                              [sentimentBuckets]="gridItem[column].sentimentPolarity.buckets"
                              [precision]="gridItem.precision"
                            ></app-sentiment-bar>
                          } @else if (gridItem[column][gridItem.scoreField] | isValidNumber) {
                            {{ gridItem[column][gridItem.scoreField] | number: gridItem.pipe }}

                            @if (gridItem.sentimentScoring === 'percentage') {
                              %
                              <app-sentiment-icon
                                [sentiment]="gridItem.percentageSentiment"
                              ></app-sentiment-icon>
                            }
                          }
                        }
                      }
                    </span>
                    @if (getCellComments(gridItem.fieldId + '_' + column); as cellComments) {
                      @if (getPinnedComment(cellComments); as pinnedComment) {
                        <div
                          class="pinned-comment-container"
                          appPinnedCommentDrag
                          [(comment)]="pinnedComment"
                          containerClass="widget-grid__table"
                          (isDragging)="disablePinnedCommentClick = $event"
                          (click)="
                            !disablePinnedCommentClick &&
                              onCellCommentIconClicked(
                                $event,
                                gridItem,
                                'fieldId',
                                gridItem.breakdownDisplay
                              )
                          "
                        >
                          <app-comment-compact-card
                            #commentCard
                            [comment]="pinnedComment"
                            [selected]="selectedCommentPointId === pinnedComment.target.pointId"
                          ></app-comment-compact-card>
                        </div>
                      } @else {
                        <xi-icon
                          icon="comment_filled"
                          class="widget-grid__table-cell--comment-icon"
                          [class.selected]="
                            selectedCommentPointId === cellComments[0].target.pointId
                          "
                          (click)="
                            onCellCommentIconClicked(
                              $event,
                              gridItem,
                              'fieldId',
                              gridItem.breakdownDisplay
                            )
                          "
                        ></xi-icon>
                      }
                    }
                  </div>
                </td>
              </ng-container>
            }
          }
          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns"
            [class.widget-grid__parent-filter-row]="
              selectedParentFilterBreakdown === (payload?.transpose ? row.fieldId : row.breakdown)
            "
          ></tr>
        </table>
      }
    </div>

    <div
      [matMenuTriggerFor]="gridMenuTrigger"
      #menuTriggerElement="matMenuTrigger"
      class="context-menu-trigger"
    ></div>
  </div>

  <mat-menu #gridMenuTrigger class="menu-with-xi-icons" xPosition="before">
    <ng-template matMenuContent let-label="itemDisplayName">
      <div mat-menu-item class="mat-menu-header" disabled>
        {{ label }}
      </div>

      <button mat-menu-item (click)="showDocumentsForItem()">
        <xi-icon icon="preview"></xi-icon>

        <div>{{ t('widgets.show-documents') }}</div>
      </button>

      @if (isSmartSummaryEnabled && !isReadOnly) {
        <div [matTooltip]="interactiveSmartSummaryMenu.tooltip">
          <button
            mat-menu-item
            (click)="openSmartSummary()"
            [disabled]="!interactiveSmartSummaryMenu.enabled"
          >
            <xi-icon
              [color]="!interactiveSmartSummaryMenu.enabled ? 'disabled' : 'orca'"
              icon="lightbulb"
            ></xi-icon>
            <div>{{ interactiveSmartSummaryMenu.label }}</div>
          </button>
        </div>
      }
      @if (isSmartSummaryEnabled && !isReadOnly && pointSummaries?.length > 0) {
        <div [matTooltip]="removeInteractiveSmartSummaryMenu.tooltip">
          <button
            mat-menu-item
            (click)="removeSmartSummary()"
            [disabled]="!removeInteractiveSmartSummaryMenu.enabled"
          >
            <xi-icon
              [color]="!removeInteractiveSmartSummaryMenu.enabled ? 'disabled' : 'orca'"
              icon="indeterminate"
            ></xi-icon>
            <div>{{ removeInteractiveSmartSummaryMenu.label }}</div>
          </button>
        </div>
      }
      @if (isSmartSummaryEnabled && !isReadOnly) {
        <div [matTooltip]="downloadSmartSummaryRequestToolTip">
          <button
            mat-menu-item
            (click)="downloadSmartSummarySpotERequest()"
            [disabled]="downloadingSpoteRequest || !canCreateSmartSummary"
          >
            <xi-icon
              [color]="downloadingSpoteRequest || !canCreateSmartSummary ? 'disabled' : 'orca'"
              icon="download"
            ></xi-icon>
            <div>{{ t('widgets.smart-summary.download-smart-summary-spote-request') }}</div>
          </button>
        </div>
      }
      @if (canAddComment) {
        <div [matTooltip]="commentDialogTooltip()">
          <button
            mat-menu-item
            [disabled]="!canOpenCommentDialog"
            [matMenuTriggerFor]="commentsMenuTrigger"
          >
            <xi-icon [color]="!canOpenCommentDialog ? 'disabled' : 'orca'" icon="comment"></xi-icon>
            <div>{{ t('widgets.comments.comments') }}</div>
          </button>
          <mat-menu #commentsMenuTrigger class="menu-with-xi-icons" xPosition="after">
            <div [matTooltip]="addCommentDialogToolTip()">
              @if (!isReadOnly) {
                <div>
                  <button mat-menu-item (click)="openCommentDialog()">
                    <xi-icon icon="add_circle"></xi-icon>
                    <div>{{ t('widgets.comments.label-add') }}</div>
                  </button>
                </div>
              }
            </div>
            <div [matTooltip]="openCommentDialogToolTip({ label })">
              <button
                mat-menu-item
                [disabled]="!hasComment({ label })"
                (click)="onCommentListOpenClicked(itemClicked)"
              >
                <xi-icon icon="app_explore"></xi-icon>
                <div>{{ t('widgets.comments.label-open') }}</div>
              </button>
            </div>
          </mat-menu>
        </div>
      }
      @if (canAddFilterToWidgetGroup) {
        <button
          mat-menu-item
          [disabled]="!hasWidgetGroups"
          (click)="showAddToWidgetGroupModal = true"
        >
          <xi-icon icon="filter"></xi-icon>
          <div>{{ t('widgets.add-to-widget-group') }}</div>
        </button>
      }
      @if (isFilterGroupParent) {
        @if (selectedParentFilterBreakdown !== pointName) {
          <button mat-menu-item (click)="onAddParentFilter()">
            <xi-icon icon="filter"></xi-icon>
            <div>{{ t('widgets.set-as-parent-filter') }}</div>
          </button>
        }
        @if (selectedParentFilterBreakdown === pointName) {
          <button mat-menu-item (click)="onRemoveParentFilter()">
            <xi-icon icon="filter"></xi-icon>
            <div>{{ t('widgets.unset-parent-filter') }}</div>
          </button>
        }
      }
    </ng-template>
  </mat-menu>

  @if (showAddToWidgetGroupModal) {
    <app-lex-xi-modal
      [isVisible]="showAddToWidgetGroupModal"
      maxWidth="720px"
      (close)="onAddFiltersToWidgetGroup(undefined)"
    >
      <ng-template appXIModalContent>
        @if (showAddToWidgetGroupModal) {
          <app-add-to-widget-group-dialog
            [dashboardViewComponent]="dashboardViewComponent"
            [widget]="widget"
            (modalClosed)="onAddFiltersToWidgetGroup($event)"
          ></app-add-to-widget-group-dialog>
        }
      </ng-template>
    </app-lex-xi-modal>
  }
  @if (showDocumentViewModal) {
    <app-lex-xi-modal
      [isVisible]="showDocumentViewModal"
      [canBackdropClose]="false"
      maxWidth="80vw"
      (close)="onDocumentViewModalClosed($event)"
    >
      <ng-template appXIModalContent>
        @if (showDocumentViewModal) {
          <app-document-view-dialog
            [options]="documentViewModalOptions"
            (modalClosed)="onDocumentViewModalClosed($event)"
          ></app-document-view-dialog>
        }
      </ng-template>
    </app-lex-xi-modal>
  }
  @if (showSmartSummaryModal) {
    <app-lex-xi-modal
      [isVisible]="showSmartSummaryModal"
      [canBackdropClose]="false"
      maxWidth="80vw"
      (close)="onSmartSummaryClosed($event)"
    >
      <ng-template appXIModalContent>
        @if (showSmartSummaryModal) {
          <app-interactive-smart-summary
            [options]="interactiveSmartSummaryOptions"
            (modalClosed)="onSmartSummaryClosed($event)"
            (addComment)="onAddSmartSummaryComment($event)"
            (addNotes)="onAddSmartSummaryNotes($event)"
          ></app-interactive-smart-summary>
        }
      </ng-template>
    </app-lex-xi-modal>
  }
</ng-container>
